import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ApiError,
  type AuditAction,
  type AuditEngagementFilters,
  type AuditEngagementPage,
  type AuditEngagementRow,
  type AuditEventsFilters,
  type AuditEventsPage,
  type AuditSummary,
} from '../api/client';

const ACTION_LABEL: Record<AuditAction, string> = {
  view: 'View',
  download: 'Download',
  pdf: 'PDF',
  proxy_view: 'View',
};

const ACTION_FILTER_OPTIONS: Array<{ value: '' | AuditAction; label: string }> = [
  { value: '', label: 'All actions' },
  { value: 'view', label: 'Views (markdown)' },
  { value: 'proxy_view', label: 'Views (HTML)' },
  { value: 'download', label: 'Downloads' },
  { value: 'pdf', label: 'PDF downloads' },
];

const PAGE_SIZE = 50;

const INPUT_CLS =
  'block w-full rounded-md border border-wc-muted/40 bg-wc-white px-2.5 py-1.5 text-sm text-wc-black placeholder:text-wc-muted focus:border-wc-blue focus:outline-none focus:ring-1 focus:ring-wc-blue/30';

const PAGER_BTN_CLS =
  'inline-flex items-center justify-center rounded-md border border-wc-blue px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-wc-blue transition-colors hover:bg-wc-blue hover:text-wc-white disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent disabled:hover:text-wc-blue';

interface DraftFilters {
  from: string;
  to: string;
  email: string;
  path: string;
  action: '' | AuditAction;
}

const EMPTY_DRAFT: DraftFilters = { from: '', to: '', email: '', path: '', action: '' };

export interface AuditDashboardContentProps {
  /**
   * Stable identifier mixed into the React-Query cache key so the portal-scoped
   * and admin-scoped dashboards never share cached responses for what is
   * conceptually different data.
   */
  scopeKey: string;
  /** Optional sub-title rendered under the page heading (e.g. customer name). */
  scopeLabel?: string;
  /**
   * Slug of the customer whose audit log is being viewed. When provided,
   * any event/engagement/user row whose `homeCustomerSlug` differs from
   * this value renders a "via {homeSlug}" tag so the operator can spot
   * cross-tenant activity from global users. Optional: omitting the prop
   * disables the tag without affecting any other behavior.
   */
  activeCustomerSlug?: string;
  getSummary: (from: string | undefined, to: string | undefined) => Promise<AuditSummary>;
  getEvents: (filters: AuditEventsFilters) => Promise<AuditEventsPage>;
  getEngagements: (filters: AuditEngagementFilters) => Promise<AuditEngagementPage>;
}

/**
 * Renders the audit dashboard body (KPI cards, top-N rollups, filter bar, paged
 * events table). Pure presentation: data fetching is delegated to the caller
 * through the two function props, which lets the same UI back both the
 * portal-scoped (`/api/v1/audit`) and the admin-scoped
 * (`/api/v1/admin/customers/:id/audit`) APIs.
 */
export function AuditDashboardContent({
  scopeKey,
  scopeLabel,
  activeCustomerSlug,
  getSummary,
  getEvents,
  getEngagements,
}: AuditDashboardContentProps) {
  const [draft, setDraft] = useState<DraftFilters>(EMPTY_DRAFT);
  const [applied, setApplied] = useState<DraftFilters>(EMPTY_DRAFT);
  const [page, setPage] = useState<number>(1);
  const [engagementPage, setEngagementPage] = useState<number>(1);
  // Section-local refinement for the "Time per user per document" table.
  // When set, these override the global email/path filters for the engagement
  // query only (they do NOT affect events or summary). Date range stays
  // controlled by the global filter bar above.
  const [engagementDraft, setEngagementDraft] = useState<{ email: string; path: string }>({
    email: '',
    path: '',
  });
  const [engagementApplied, setEngagementApplied] = useState<{ email: string; path: string }>({
    email: '',
    path: '',
  });

  const filters: AuditEventsFilters = useMemo(
    () => ({
      from: applied.from || undefined,
      to: applied.to || undefined,
      email: applied.email || undefined,
      path: applied.path || undefined,
      action: applied.action || undefined,
      page,
      pageSize: PAGE_SIZE,
    }),
    [applied, page],
  );

  const engagementFilters: AuditEngagementFilters = useMemo(
    () => ({
      from: applied.from || undefined,
      to: applied.to || undefined,
      // Section-local filters refine (override) the global email/path for
      // engagement only. Falling back to the global value when empty preserves
      // the previous "global filters drive everything" default for users who
      // never touch the section-local inputs.
      email: engagementApplied.email || applied.email || undefined,
      path: engagementApplied.path || applied.path || undefined,
      page: engagementPage,
      pageSize: PAGE_SIZE,
    }),
    [applied, engagementApplied, engagementPage],
  );

  const summaryQuery = useQuery({
    queryKey: ['audit', scopeKey, 'summary', applied.from, applied.to],
    queryFn: () => getSummary(applied.from || undefined, applied.to || undefined),
  });

  const eventsQuery = useQuery({
    queryKey: ['audit', scopeKey, 'events', filters],
    queryFn: () => getEvents(filters),
  });

  const engagementsQuery = useQuery({
    queryKey: ['audit', scopeKey, 'engagements', engagementFilters],
    queryFn: () => getEngagements(engagementFilters),
  });

  const onApply = () => {
    setApplied(draft);
    setPage(1);
    setEngagementPage(1);
  };
  const onReset = () => {
    setDraft(EMPTY_DRAFT);
    setApplied(EMPTY_DRAFT);
    setEngagementDraft({ email: '', path: '' });
    setEngagementApplied({ email: '', path: '' });
    setPage(1);
    setEngagementPage(1);
  };
  const onApplyEngagement = () => {
    setEngagementApplied(engagementDraft);
    setEngagementPage(1);
  };
  const onResetEngagement = () => {
    setEngagementDraft({ email: '', path: '' });
    setEngagementApplied({ email: '', path: '' });
    setEngagementPage(1);
  };

  const totals = summaryQuery.data?.totals;
  const totalEvents = eventsQuery.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalEvents / PAGE_SIZE));
  const totalEngagements = engagementsQuery.data?.total ?? 0;
  const engagementTotalPages = Math.max(1, Math.ceil(totalEngagements / PAGE_SIZE));

  return (
    <>
      <header className="mb-6">
        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-wc-muted-strong">
          Audit
        </p>
        <h1 className="font-serif text-3xl text-wc-black">File access dashboard</h1>
        <p className="mt-1 text-sm text-wc-muted-strong">
          Who opened which documents, when, and how long they spent on them.
          {scopeLabel && (
            <span className="ml-2 inline-flex items-center rounded-full bg-wc-blue/10 px-2.5 py-0.5 text-xs font-medium text-wc-blue">
              {scopeLabel}
            </span>
          )}
        </p>
      </header>

      <section className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <KpiCard
          label="Total views"
          value={
            totals ? (totals.views + totals.proxyViews).toLocaleString() : '—'
          }
        />
        <KpiCard
          label="Downloads"
          value={totals ? (totals.downloads + totals.pdfs).toLocaleString() : '—'}
        />
        <KpiCard label="Unique users" value={totals ? totals.uniqueUsers.toLocaleString() : '—'} />
        <KpiCard
          label="Avg time / document"
          value={totals ? formatDuration(totals.avgEngagementMs ?? 0) : '—'}
        />
      </section>

      <section className="mb-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <TopDocumentsCard summary={summaryQuery.data} loading={summaryQuery.isLoading} />
        <TopUsersCard
          summary={summaryQuery.data}
          loading={summaryQuery.isLoading}
          activeCustomerSlug={activeCustomerSlug}
        />
      </section>

      <section className="card mb-6 overflow-hidden">
        <div className="border-b border-wc-muted/15 bg-wc-gray/40 px-5 py-4">
          <div className="flex items-baseline justify-between gap-4">
            <div>
              <h2 className="text-sm font-semibold text-wc-black">
                Time per user per document
              </h2>
              <p className="text-xs text-wc-muted-strong">
                {totalEngagements.toLocaleString()} (user, document) pairs.
                Date range comes from the filter bar above; refine this table by
                user or document below.
              </p>
            </div>
          </div>
          <form
            className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-[1fr_1fr_auto_auto]"
            onSubmit={(e) => {
              e.preventDefault();
              onApplyEngagement();
            }}
          >
            <FilterField label="Filter by user (email)">
              <input
                type="text"
                value={engagementDraft.email}
                onChange={(e) =>
                  setEngagementDraft({ ...engagementDraft, email: e.target.value })
                }
                placeholder="alice@…"
                className={INPUT_CLS}
              />
            </FilterField>
            <FilterField label="Filter by document (path)">
              <input
                type="text"
                value={engagementDraft.path}
                onChange={(e) =>
                  setEngagementDraft({ ...engagementDraft, path: e.target.value })
                }
                placeholder="docs/intro.md"
                className={INPUT_CLS}
              />
            </FilterField>
            <div className="flex items-end gap-2">
              <button
                type="submit"
                className="inline-flex items-center justify-center rounded-md bg-wc-blue px-3 py-2 text-xs font-semibold uppercase tracking-wider text-wc-white transition-colors hover:bg-wc-blue-dark"
              >
                Apply
              </button>
            </div>
            <div className="flex items-end gap-2">
              <button
                type="button"
                className="inline-flex items-center justify-center rounded-md border border-wc-blue px-3 py-2 text-xs font-semibold uppercase tracking-wider text-wc-blue transition-colors hover:bg-wc-blue hover:text-wc-white"
                onClick={onResetEngagement}
              >
                Reset
              </button>
            </div>
          </form>
        </div>

        <EngagementTable
          loading={engagementsQuery.isLoading}
          error={engagementsQuery.error}
          rows={engagementsQuery.data?.items ?? []}
          activeCustomerSlug={activeCustomerSlug}
        />

        <div className="flex items-center justify-between gap-2 border-t border-wc-muted/15 bg-wc-gray/30 px-5 py-3 text-xs text-wc-muted-strong">
          <p>
            Page {engagementPage} of {engagementTotalPages}
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              className={PAGER_BTN_CLS}
              disabled={engagementPage <= 1}
              onClick={() => setEngagementPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </button>
            <button
              type="button"
              className={PAGER_BTN_CLS}
              disabled={engagementPage >= engagementTotalPages}
              onClick={() =>
                setEngagementPage((p) => Math.min(engagementTotalPages, p + 1))
              }
            >
              Next
            </button>
          </div>
        </div>
      </section>

      <section className="card overflow-hidden">
        <div className="border-b border-wc-muted/15 bg-wc-gray/40 px-5 py-4">
          <h2 className="text-sm font-semibold text-wc-black">Events</h2>
          <p className="text-xs text-wc-muted-strong">
            {totalEvents.toLocaleString()} total matching events.
          </p>
          <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-6">
            <FilterField label="From">
              <input
                type="date"
                value={draft.from}
                onChange={(e) => setDraft({ ...draft, from: e.target.value })}
                className={INPUT_CLS}
              />
            </FilterField>
            <FilterField label="To">
              <input
                type="date"
                value={draft.to}
                onChange={(e) => setDraft({ ...draft, to: e.target.value })}
                className={INPUT_CLS}
              />
            </FilterField>
            <FilterField label="User (email)">
              <input
                type="text"
                value={draft.email}
                onChange={(e) => setDraft({ ...draft, email: e.target.value })}
                placeholder="alice@…"
                className={INPUT_CLS}
              />
            </FilterField>
            <FilterField label="Path contains">
              <input
                type="text"
                value={draft.path}
                onChange={(e) => setDraft({ ...draft, path: e.target.value })}
                placeholder="docs/"
                className={INPUT_CLS}
              />
            </FilterField>
            <FilterField label="Action">
              <select
                value={draft.action}
                onChange={(e) =>
                  setDraft({ ...draft, action: e.target.value as '' | AuditAction })
                }
                className={INPUT_CLS}
              >
                {ACTION_FILTER_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </FilterField>
            <div className="flex items-end gap-2">
              <button
                type="button"
                className="inline-flex items-center justify-center rounded-md bg-wc-blue px-3 py-2 text-xs font-semibold uppercase tracking-wider text-wc-white transition-colors hover:bg-wc-blue-dark"
                onClick={onApply}
              >
                Apply
              </button>
              <button
                type="button"
                className="inline-flex items-center justify-center rounded-md border border-wc-blue px-3 py-2 text-xs font-semibold uppercase tracking-wider text-wc-blue transition-colors hover:bg-wc-blue hover:text-wc-white"
                onClick={onReset}
              >
                Reset
              </button>
            </div>
          </div>
        </div>

        <EventsTable
          loading={eventsQuery.isLoading}
          error={eventsQuery.error}
          rows={eventsQuery.data?.items ?? []}
          activeCustomerSlug={activeCustomerSlug}
        />

        <div className="flex items-center justify-between gap-2 border-t border-wc-muted/15 bg-wc-gray/30 px-5 py-3 text-xs text-wc-muted-strong">
          <p>
            Page {page} of {totalPages}
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              className={PAGER_BTN_CLS}
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </button>
            <button
              type="button"
              className={PAGER_BTN_CLS}
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Next
            </button>
          </div>
        </div>
      </section>
    </>
  );
}

interface KpiCardProps {
  label: string;
  value: string;
}

function KpiCard({ label, value }: KpiCardProps) {
  return (
    <div className="card px-5 py-4">
      <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-wc-muted-strong">
        {label}
      </p>
      <p className="mt-2 text-2xl font-semibold text-wc-black">{value}</p>
    </div>
  );
}

interface FilterFieldProps {
  label: string;
  children: React.ReactNode;
}

function FilterField({ label, children }: FilterFieldProps) {
  return (
    <label className="flex flex-col gap-1 text-xs text-wc-muted-strong">
      <span className="font-medium">{label}</span>
      {children}
    </label>
  );
}

interface EventsTableProps {
  loading: boolean;
  error: unknown;
  rows: Array<{
    id: string;
    email: string;
    displayName: string | null;
    path: string;
    action: AuditAction;
    createdAt: string;
    homeCustomerSlug?: string;
  }>;
  activeCustomerSlug?: string;
}

function EventsTable({ loading, error, rows, activeCustomerSlug }: EventsTableProps) {
  if (loading) {
    return <p className="px-5 py-8 text-center text-sm text-wc-muted">Loading…</p>;
  }
  if (error) {
    const msg =
      error instanceof ApiError && error.status === 403
        ? 'Access denied.'
        : 'Failed to load events.';
    return <p className="px-5 py-8 text-center text-sm text-wc-red">{msg}</p>;
  }
  if (rows.length === 0) {
    return (
      <p className="px-5 py-8 text-center text-sm text-wc-muted">
        No events match the current filters.
      </p>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-wc-muted/15 text-sm">
        <thead className="bg-wc-gray/30 text-left text-xs uppercase tracking-wider text-wc-muted-strong">
          <tr>
            <th className="px-5 py-2 font-medium">When</th>
            <th className="px-5 py-2 font-medium">User</th>
            <th className="px-5 py-2 font-medium">Action</th>
            <th className="px-5 py-2 font-medium">Path</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-wc-muted/10">
          {rows.map((row) => (
            <tr key={row.id} className="hover:bg-wc-gray/30">
              <td className="whitespace-nowrap px-5 py-2 text-xs text-wc-muted-strong">
                {new Date(row.createdAt).toLocaleString()}
              </td>
              <td className="px-5 py-2">
                <div className="flex items-center gap-1.5">
                  <span className="font-medium text-wc-black">
                    {row.displayName ?? row.email}
                  </span>
                  <ViaTag
                    homeCustomerSlug={row.homeCustomerSlug}
                    activeCustomerSlug={activeCustomerSlug}
                  />
                </div>
                {row.displayName && (
                  <div className="text-xs text-wc-muted-strong">{row.email}</div>
                )}
              </td>
              <td className="px-5 py-2 text-xs">
                <ActionBadge action={row.action} />
              </td>
              <td className="px-5 py-2 font-mono text-xs text-wc-black" title={row.path}>
                {row.path}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface EngagementTableProps {
  loading: boolean;
  error: unknown;
  rows: AuditEngagementRow[];
  activeCustomerSlug?: string;
}

function EngagementTable({
  loading,
  error,
  rows,
  activeCustomerSlug,
}: EngagementTableProps) {
  if (loading) {
    return <p className="px-5 py-8 text-center text-sm text-wc-muted">Loading…</p>;
  }
  if (error) {
    const msg =
      error instanceof ApiError && error.status === 403
        ? 'Access denied.'
        : 'Failed to load engagement data.';
    return <p className="px-5 py-8 text-center text-sm text-wc-red">{msg}</p>;
  }
  if (rows.length === 0) {
    return (
      <p className="px-5 py-8 text-center text-sm text-wc-muted">
        No engagement data recorded yet for these filters.
      </p>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-wc-muted/15 text-sm">
        <thead className="bg-wc-gray/30 text-left text-xs uppercase tracking-wider text-wc-muted-strong">
          <tr>
            <th className="px-5 py-2 font-medium">User</th>
            <th className="px-5 py-2 font-medium">Document</th>
            <th className="px-5 py-2 text-right font-medium">Time spent</th>
            <th className="px-5 py-2 text-right font-medium">Sessions</th>
            <th className="px-5 py-2 font-medium">Last seen</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-wc-muted/10">
          {rows.map((row) => (
            <tr
              key={`${row.portalUserId}|${row.path}`}
              className="hover:bg-wc-gray/30"
            >
              <td className="px-5 py-2">
                <div className="flex items-center gap-1.5">
                  <span className="font-medium text-wc-black">
                    {row.displayName ?? row.email}
                  </span>
                  <ViaTag
                    homeCustomerSlug={row.homeCustomerSlug}
                    activeCustomerSlug={activeCustomerSlug}
                  />
                </div>
                {row.displayName && (
                  <div className="text-xs text-wc-muted-strong">{row.email}</div>
                )}
              </td>
              <td className="px-5 py-2 font-mono text-xs text-wc-black" title={row.path}>
                {row.path}
              </td>
              <td className="px-5 py-2 text-right tabular-nums text-wc-black">
                {formatDuration(row.totalMs)}
              </td>
              <td className="px-5 py-2 text-right tabular-nums text-wc-muted-strong">
                {row.sessions}
              </td>
              <td className="whitespace-nowrap px-5 py-2 text-xs text-wc-muted-strong">
                {new Date(row.lastSeenAt).toLocaleString()}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ActionBadge({ action }: { action: AuditAction }) {
  const isDownload = action === 'download' || action === 'pdf';
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${
        isDownload
          ? 'bg-wc-blue/10 text-wc-blue'
          : 'bg-wc-gray text-wc-muted-strong'
      }`}
    >
      {ACTION_LABEL[action]}
    </span>
  );
}

interface TopDocumentsCardProps {
  summary: AuditSummary | undefined;
  loading: boolean;
}

function TopDocumentsCard({ summary, loading }: TopDocumentsCardProps) {
  return (
    <div className="card overflow-hidden">
      <div className="border-b border-wc-muted/15 bg-wc-gray/40 px-5 py-3">
        <h2 className="text-sm font-semibold text-wc-black">Top documents</h2>
      </div>
      <div className="p-4">
        {loading ? (
          <p className="text-sm text-wc-muted">Loading…</p>
        ) : !summary || summary.topDocuments.length === 0 ? (
          <p className="text-sm text-wc-muted">No data yet.</p>
        ) : (
          <ul className="divide-y divide-wc-muted/10">
            {summary.topDocuments.map((doc) => (
              <li key={doc.path} className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-4 py-2 text-xs">
                <span className="truncate font-mono text-wc-black" title={doc.path}>
                  {doc.path}
                </span>
                <span className="text-wc-muted-strong">{doc.views} views</span>
                <span className="text-wc-muted-strong">{doc.downloads} dl</span>
                <span className="text-wc-muted-strong">{formatDuration(doc.totalMs)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

interface TopUsersCardProps {
  summary: AuditSummary | undefined;
  loading: boolean;
  activeCustomerSlug?: string;
}

function TopUsersCard({ summary, loading, activeCustomerSlug }: TopUsersCardProps) {
  return (
    <div className="card overflow-hidden">
      <div className="border-b border-wc-muted/15 bg-wc-gray/40 px-5 py-3">
        <h2 className="text-sm font-semibold text-wc-black">Top users</h2>
      </div>
      <div className="p-4">
        {loading ? (
          <p className="text-sm text-wc-muted">Loading…</p>
        ) : !summary || summary.topUsers.length === 0 ? (
          <p className="text-sm text-wc-muted">No data yet.</p>
        ) : (
          <ul className="divide-y divide-wc-muted/10">
            {summary.topUsers.map((user) => (
              <li key={user.email} className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-4 py-2 text-xs">
                <span className="flex min-w-0 items-center gap-1.5 text-wc-black" title={user.email}>
                  <span className="truncate">{user.displayName ?? user.email}</span>
                  <ViaTag
                    homeCustomerSlug={user.homeCustomerSlug}
                    activeCustomerSlug={activeCustomerSlug}
                  />
                </span>
                <span className="text-wc-muted-strong">{user.views} views</span>
                <span className="text-wc-muted-strong">{user.downloads} dl</span>
                <span className="text-wc-muted-strong">{formatDuration(user.totalMs)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

interface ViaTagProps {
  homeCustomerSlug?: string;
  activeCustomerSlug?: string;
}

/**
 * Renders a small "via {homeSlug}" pill next to a user's name when their
 * home tenant differs from the customer whose audit dashboard is being
 * viewed. Suppressed (returns null) when either slug is missing or when
 * the actor's home matches the active tenant -- so non-global activity
 * keeps its original compact rendering.
 */
function ViaTag({ homeCustomerSlug, activeCustomerSlug }: ViaTagProps) {
  if (!homeCustomerSlug || !activeCustomerSlug) return null;
  if (homeCustomerSlug === activeCustomerSlug) return null;
  return (
    <span
      className="inline-flex shrink-0 items-center rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-800"
      title={`Acting from home customer "${homeCustomerSlug}"`}
    >
      via {homeCustomerSlug}
    </span>
  );
}

function formatDuration(ms: number): string {
  if (!ms || ms < 1000) return '<1s';
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remSec = seconds % 60;
  if (minutes < 60) return remSec === 0 ? `${minutes}m` : `${minutes}m ${remSec}s`;
  const hours = Math.floor(minutes / 60);
  const remMin = minutes % 60;
  return remMin === 0 ? `${hours}h` : `${hours}h ${remMin}m`;
}
